import React from "react";
import { Trash2, Minus, Plus, User2, X } from "lucide-react";
import { TID } from "@/constants/testIds";

export default function CartPanel({
  sale,
  onRemove,
  onChangeQty,
  onClear,
  onPay,
  onPark,
  onSelectCustomer,
}) {
  const lines = sale?.lines || [];
  const total = sale?.total || 0;
  const taxes = sale?.tax_total || 0;
  const sub = sale?.subtotal || 0;
  const discount = sale?.discount_total || 0;

  return (
    <div data-testid={TID.posCart} className="h-full flex flex-col bg-white border-r border-slate-200">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-medium">Ticket actual</div>
            <div className="font-display text-lg font-semibold text-slate-900">
              {sale?.serie || "T"} · borrador
            </div>
          </div>
          <button
            data-testid={TID.posCustomerBtn}
            onClick={onSelectCustomer}
            className="h-10 px-3 rounded-md border border-slate-200 hover:border-slate-400 text-sm flex items-center gap-1.5"
          >
            <User2 size={14} />
            <span className="max-w-[140px] truncate">{sale?.customer_name || "Cliente"}</span>
          </button>
        </div>
      </div>

      {/* Lines */}
      <div className="flex-1 overflow-y-auto thin-scroll">
        {lines.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 px-4 text-center">
            <div className="text-5xl mb-2">🛒</div>
            <div className="font-display text-base">Sin artículos</div>
            <div className="text-xs mt-1">Toca un producto o escanea un código de barras</div>
          </div>
        )}
        {lines.map((l) => (
          <div
            key={l.line_id}
            data-testid={TID.posCartLine(l.line_id)}
            className="cart-row-enter px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-slate-800 truncate">{l.name}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                  {l.sku} · {(l.unit_price).toFixed(2)}€/ud · IVA {(l.tax_rate * 100).toFixed(0)}%
                </div>
              </div>
              <div className="font-mono font-semibold text-slate-900 text-right">
                {l.subtotal.toFixed(2)}€
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-1">
                <button
                  data-testid={TID.posCartLineMinus(l.line_id)}
                  onClick={() => onChangeQty(l, Math.max(0, l.qty - 1))}
                  className="h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                >
                  <Minus size={14} />
                </button>
                <div className="h-8 min-w-[44px] px-2 rounded-md bg-slate-100 font-mono text-sm flex items-center justify-center">
                  {l.qty}
                </div>
                <button
                  data-testid={TID.posCartLinePlus(l.line_id)}
                  onClick={() => onChangeQty(l, l.qty + 1)}
                  className="h-8 w-8 rounded-md border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                data-testid={TID.posCartLineRemove(l.line_id)}
                onClick={() => onRemove(l)}
                className="h-8 px-2 rounded-md text-red-600 hover:bg-red-50 flex items-center gap-1 text-xs"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Totals + actions */}
      <div className="border-t border-slate-200 p-4 bg-slate-50">
        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Base imponible</span>
            <span className="font-mono">{sub.toFixed(2)}€</span>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-amber-700">
              <span>Descuento</span>
              <span className="font-mono">-{discount.toFixed(2)}€</span>
            </div>
          )}
          <div className="flex justify-between text-slate-600">
            <span>IVA</span>
            <span className="font-mono">{taxes.toFixed(2)}€</span>
          </div>
          <div data-testid={TID.posTotal} className="flex justify-between items-end pt-2 mt-2 border-t border-slate-200">
            <span className="font-display text-base font-medium text-slate-900">TOTAL</span>
            <span className="font-mono text-3xl font-semibold text-slate-900">{total.toFixed(2)}€</span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          <button
            data-testid={TID.posCancelBtn}
            onClick={onClear}
            disabled={lines.length === 0}
            className="h-12 rounded-md bg-red-50 text-red-700 font-medium text-sm hover:bg-red-100 disabled:opacity-40 flex items-center justify-center gap-1.5"
          >
            <X size={16} /> Anular
          </button>
          <button
            data-testid={TID.posParkBtn}
            onClick={onPark}
            disabled={lines.length === 0}
            className="h-12 rounded-md bg-amber-50 text-amber-800 font-medium text-sm hover:bg-amber-100 disabled:opacity-40"
          >
            Aparcar
          </button>
          <button
            data-testid={TID.posPayBtn}
            onClick={onPay}
            disabled={lines.length === 0}
            className="h-12 rounded-md bg-green-600 text-white font-display font-semibold text-base hover:bg-green-700 disabled:bg-slate-300"
          >
            Cobrar
          </button>
        </div>
      </div>
    </div>
  );
}
